<script setup lang="ts">
import { computed, inject, useAttrs } from "vue";
import type { ButtonType, ControlSize } from "../models/types";
import {
  CONTROLS_DESIGN_VERSION,
  DEFAULT_CONTROLS_DESIGN_VERSION,
  DEFAULT_DESIGN_VERSION,
  DESIGN_VERSION,
} from "../models";

export type BcButtonProps = {
  /** Disables button for click */
  disabled?: boolean;
  /** Expands the button to the width of its parent container */
  fullWidth?: boolean;
  /** Expands the button to the height of its parent container */
  fullHeight?: boolean;
  /** Removes all background styles */
  outlined?: boolean;
  /** Removes all background and border styles */
  plain?: boolean;
  /** Makes the button's border rounded */
  rounded?: boolean;
  /** Specifies pre-defined sizes for the button */
  size?: ControlSize;
  /** Specifies the type of button */
  type?: ButtonType;
};

defineOptions({ inheritAttrs: false });

const attrs = useAttrs();

const {
  disabled = false,
  fullWidth = false,
  fullHeight = false,
  outlined = false,
  plain = false,
  rounded = false,
  size,
  type = "button",
} = defineProps<BcButtonProps>();

const computedTag = computed<string>(() => {
  if (attrs.href !== undefined && attrs.href !== null && typeof attrs.href === "string") {
    return "a";
  }
  if (
    attrs.to !== undefined &&
    attrs.to !== null &&
    (typeof attrs.to === "string" || typeof attrs.to === "object")
  ) {
    return "router-link";
  }
  return "button";
});

const design = inject(
  DESIGN_VERSION,
  computed(() => DEFAULT_DESIGN_VERSION),
);
const controlsDesign = inject(
  CONTROLS_DESIGN_VERSION,
  computed(() => DEFAULT_CONTROLS_DESIGN_VERSION),
);
const pak = computed(() => `bc-controls-d${controlsDesign.value}`);

defineExpose({
  attrs,
  computedTag,
});
</script>

<template>
  <component
    v-bind:is="computedTag"
    v-bind:data-pak="pak"
    v-bind:data-design="design"
    class="bissantzControls buttonControl"
    v-bind="$attrs"
    v-bind:disabled="disabled"
    v-bind:type="computedTag === 'button' ? type : undefined"
    v-bind:class="[
      size ? `button-${size}` : '',
      {
        disabled: disabled,
        fullWidth: fullWidth,
        fullHeight: fullHeight,
        outlined: outlined,
        plain: plain,
        rounded: rounded,
      },
    ]"
  >
    <slot />
  </component>
</template>

<style lang="less">
@import "../styles/components/bc-button.less";
</style>
